import { resolveWithdrawalSourceTokenBalance } from '@funkit/connect-core'
import { useEffect, useRef, useState } from 'react'

import type {
  WithdrawalFlowConfig,
  WithdrawalSourceTokenParams,
} from '../flow/withdrawalFlowConfig'
import { logger } from '../logging/logger'

type BalanceCallback = NonNullable<
  WithdrawalFlowConfig['withdrawalSourceTokenBalance']
>

interface Settled {
  key: string
  value: string
}

interface Pending {
  key: string
  generation: number
}

const tokenKey = ({ sourceToken }: WithdrawalSourceTokenParams) =>
  `${sourceToken.chainId}:${sourceToken.address}`

/**
 * The host's source balance, whichever way it answers. A plain value is read
 * on render as before. A promise is awaited once per source token and the
 * balance reads as zero until it lands: the form must not offer a Max the
 * host has not confirmed. Undefined only when the host gave no callback.
 */
export function useWithdrawalSourceTokenBalance(
  callback: BalanceCallback | undefined,
  params: WithdrawalSourceTokenParams,
): string | undefined {
  const key = tokenKey(params)
  const [settled, setSettled] = useState<Settled>()
  // The token whose read is in flight; a token switched away from mid-read
  // drops its answer rather than showing it against the new token.
  const pending = useRef<Pending>(undefined)
  const generation = useRef(0)
  const asyncCallbacks = useRef(new WeakSet<BalanceCallback>())
  const mounted = useRef(true)
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  const settle = (forKey: string, promise: Promise<string | number>) => {
    const requestGeneration = generation.current + 1
    generation.current = requestGeneration
    pending.current = { key: forKey, generation: requestGeneration }
    void promise
      .then(validate, (error: unknown) => {
        logger.warn('withdrawal:sourceTokenBalanceThrew', {
          error: error instanceof Error ? error.message : String(error),
        })
        return '0'
      })
      .then((value) => {
        if (pending.current?.generation !== requestGeneration) {
          return
        }
        pending.current = undefined
        if (mounted.current) {
          setSettled({ key: forKey, value })
        }
      })
  }

  const isAsync = callback !== undefined && asyncCallbacks.current.has(callback)
  let read: string | undefined
  if (callback !== undefined && !isAsync) {
    read = resolveWithdrawalSourceTokenBalance(
      (p: WithdrawalSourceTokenParams) => {
        const raw = callback(p)
        if (!isThenable(raw)) {
          return raw
        }
        // The first promise is the read; from here the effect owns re-reads.
        asyncCallbacks.current.add(callback)
        settle(key, raw)
        return '0'
      },
      params,
      logger,
    )
  }

  useEffect(() => {
    if (callback === undefined || !asyncCallbacks.current.has(callback)) {
      return
    }
    if (settled?.key === key || pending.current?.key === key) {
      return
    }
    settle(
      key,
      Promise.resolve().then(() => callback(params)),
    )
    // `settle` closes over refs only; re-running on it would restart reads.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callback, key, params, settled?.key])

  if (callback === undefined) {
    return undefined
  }
  if (isAsync) {
    return settled?.key === key ? settled.value : '0'
  }
  return read
}

function isThenable(value: unknown): value is Promise<string | number> {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as { then?: unknown }).then === 'function'
  )
}

/** Same bounds as the plain path, so an awaited answer is no more trusted. */
function validate(raw: string | number): string {
  return (
    resolveWithdrawalSourceTokenBalance(() => raw, undefined, logger) ?? '0'
  )
}
